import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { editProject, editProjectClass, getImagesByClass, getProject } from "../services/projectService";
import {
  Box,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Collapse,
  Tooltip,
} from "@mui/material";
import { IImageInfo } from "@types";
import { formatHourByTime } from "../utils/utils";
import { extractFileName, getPathImage, getProjectGeneralInfo, truncateString } from "common-lib";
import { t } from "i18next";
import { ArrowBackIosIcon, ArrowForwardIosIcon, CheckIcon, CloseIcon, EditIcon, ModelTrainingIcon } from "../icons";
import CustomButton from "../components/customComponent/CustomButton";
import { getTrainStatus } from "../services/trainService";
import { useRecoilState } from "recoil";
import { trainStatusState, dataLoadingState } from "../recoil";
import { IProjectData } from "@types";
// import { Progress } from "antd";
import AnalysisServerModal from "../components/project/AnalysisServerModal";
import { getAIInfo } from "../services/serverService";
import { BASE_URL } from "../services/client";
import CircularWithVariable from "../components/CircularWithVariable";
import { useTutorial } from "../context/TutorialContext";
import { useProjectImagePolling } from "../hooks/useProjectImagePolling";

interface ClassCardProps {
  item: ImagesByClass;
  index: number;
  onDetail: (label: string) => void;
  onRow: (image: SimpleImageByClass) => void;
  t: () => void;
}

const previewModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "580px",
  height: "580px",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  userSelect: "none",
};

const arrowButtonStyle = {
  position: "absolute",
  top: "50%",
  zIndex: 2,
  transform: "translateY(-50%)",
  width: "42px",
  height: "42px",
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  padding: 0,
  backgroundColor: "#ccc",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
};

interface ImagesByClass {
  label: string;
  count: number;
  images: SimpleImageByClass[];
}

interface SimpleImageByClass {
  id: number;
  path: string;
}

const ClassImageList = React.memo(
  ({ item, index, onDetail, onRow, t }: ClassCardProps) => (
    <Box
      key={index}
      sx={{
        width: 340,
        height: 620,
        borderRadius: 3,
        flexShrink: 0,
        scrollSnapAlign: "start",
        backgroundColor: "var(--dk-surface, #fff)",
        boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
        border: "1px solid var(--dk-border, #eee)",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
        },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid var(--dk-border, #eee)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "linear-gradient(135deg, #F0F4FF 0%, #F9FAFF 100%)",
          flexShrink: 0,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "4px",
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            backgroundColor: ["#4F46E5", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4"][index % 5],
          },
        }}
      >
        <Box sx={{ ml: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--dk-text, #1F2937)" }}>{item.label} Class</Typography>
          <Typography sx={{ fontSize: 12, color: "var(--dk-text-2, #6B7280)" }}>{item.count}장</Typography>
        </Box>
        <Button
          id="ana-table-enter"
          variant="contained"
          size="small"
          onClick={() => onDetail(item.label)}
          sx={{
            backgroundColor: "#5b94ff",
            "&:hover": { backgroundColor: "#5b94ff" },
          }}
        >
          Detail
        </Button>
      </Box>

      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table>
          <TableBody>
            {item.images?.map((image: SimpleImageByClass) => (
              <TableRow
                key={image.id}
                onClick={() => onRow(image)}
                sx={{
                  cursor: "pointer",
                  transition: "background 0.15s",
                  "&:hover": { backgroundColor: "#F5F7FB" },
                }}
              >
                <TableCell sx={{ color: "var(--dk-text-2, #555)", width: 80 }}>{image.id}</TableCell>
                <TableCell sx={{ color: "var(--dk-text, #333)" }}>{truncateString(extractFileName(image.path), 20)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ),
  (prev, next) =>
    prev.item.label === next.item.label &&
    prev.item.count === next.item.count &&
    prev.index === next.index &&
    prev.item.images?.length === next.item.images?.length,
);

export default function AnalysisMainPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classList, setClassList] = useState<string[]>([]);
  const [imageList, setImageList] = useState<ImagesByClass[]>([{ label: "", count: 0, images: [{ id: 0, path: "" }] }]);
  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [trainStatus, setTrainStatus] = useRecoilState(trainStatusState);
  const [editClassMode, setEditClassMode] = useState<{
    [key: string]: boolean;
  }>({});
  const [editedLabel, setEditedLabel] = useState<string>("");
  const [selectedRow, setSelectedRow] = useState<SimpleImageByClass>({
    id: -1,
    path: "",
  });
  const [loading, setLoading] = useState(false);
  const [pollingLoading, setPollingLoading] = useState(false);
  const location = useLocation();
  const totalFromState = location.state?.totalFileCount ?? 0;
  const [totalFileCount, setTotalFileCount] = useState(totalFromState);
  const [loadedImageCount, setLoadedImageCount] = useState(0);
  const [openStorageModal, setOpenStorageModal] = useState<boolean>(false);

  const trainStateList = ["", "None", "Init", "Calc", "Train", "Stopping"];

  const [trainTime, setTrainTime] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [trainPercent, setTrainPercent] = useState<number>(0);
  const { pendingStartRef, startTour, tutorialKey, tutorialKeyRef } = useTutorial();

  useEffect(() => {
    loadProjectClassImage();
    fetchAIServer();

    if (totalFromState === 0) {
      fetchProjectData();
    }
  }, []);

  useEffect(() => {
    if (pendingStartRef.current) {
      pendingStartRef.current = false;
      startTour(tutorialKeyRef.current);
    }
  }, []);

  useEffect(() => {
    if (totalFileCount > 0 && loadedImageCount < totalFileCount) {
      start();
    }
  }, [totalFileCount, loadedImageCount]);

  const stalePollCountRef = useRef(0);
  const prevLoadedCountRef = useRef<number | null>(null);

  const loadProjectClassImage = async () => {
    if (isPolling) setPollingLoading(true);
    else setLoading(true);

    try {
      const response = await getImagesByClass(Number(id));
      const loaded = (response.images as ImagesByClass[]).reduce(
        (acc: number, c: ImagesByClass) => acc + (c.count ?? 0),
        0,
      );
      if (response) {
        setClassList(response.classList);
        setImageList(response.images);
        setLoadedImageCount(loaded);
      }

      if (isPolling) {
        if (prevLoadedCountRef.current === loaded) {
          stalePollCountRef.current += 1;
          if (stalePollCountRef.current >= 5) stop();
        } else {
          stalePollCountRef.current = 0;
          prevLoadedCountRef.current = loaded;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setPollingLoading(false);
      console.log("finish", loading, pollingLoading);
    }
  };
  const { isPolling, start, stop } = useProjectImagePolling(loadProjectClassImage, totalFileCount, loadedImageCount);

  const fetchProjectData = async () => {
    const response = await getProjectGeneralInfo(Number(id));
    setTotalFileCount(response.project.counts.label_count.total ?? 0);
  };

  const fetchAIServer = async () => {
    let eventSource: EventSource | null = null;

    try {
      const response = await getAIInfo();
      const filteredAI = response.data.filter((item: any) => item.train.project_id === Number(id));
      // 해당 프로젝트가 학습/검사 중인 경우
      if (filteredAI.length > 0) {
        const url = `${BASE_URL}/ai-info-server?id=${id}&server=${filteredAI[0].server}`;
        eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          setTrainStatus(data);
          if (data.state === "none" || data.state === "end") {
            console.log("Close SSE connection");
            eventSource?.close();
          }
        };

        eventSource.onerror = (error) => {
          console.error("SSE 오류:", error);
          eventSource?.close();
        };
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowClick = useCallback((row: SimpleImageByClass) => {
    setOpenPreview(true);
    setSelectedRow(row);
  }, []);

  const handleDetailClick = useCallback(
    (label: string) => {
      try {
        navigate(`/main/project/${id}/analysis/${label}`);
      } catch (error) {
        console.error(error);
      }
    },
    [navigate, id],
  );

  const PreviewModal = () => {
    type ImageData = string | null;
    const [imageData, setImageData] = useState<ImageData>(null);

    const loadPreviewImage = async () => {
      try {
        const response = await getPathImage(selectedRow.path);
        setImageData(response);
      } catch (error) {
        console.error(error);
      }
    };

    const handleClose = () => {
      setOpenPreview(false);
      setSelectedRow({
        path: "",
        // label: "",
        // score: 0,
        id: -1,
      });
    };

    useEffect(() => {
      loadPreviewImage();
    }, []);

    return (
      <Modal open={openPreview} sx={previewModalStyle}>
        <Box sx={previewModalStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ ml: "5px" }}>{extractFileName(selectedRow.path)}</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          {imageData && (
            <img
              src={imageData}
              alt="Loaded"
              style={{
                padding: "5px",
                width: "100%",
                height: "530px",
              }}
            />
          )}
        </Box>
      </Modal>
    );
  };

  const handleEditClick = (label: string, index: number) => {
    console.log(index);
    setEditedLabel(label);
    setEditClassMode((prevState) => ({
      ...prevState,
      [label]: true,
    }));
  };

  // TextField 값이 변경될 때 호출되는 함수
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedLabel(e.target.value);
  };

  // CheckButton 클릭 시 호출되는 함수
  const handleCheckClick = (oldLabel: string, index: number) => {
    handleChange(oldLabel, index, editedLabel);
    setEditedLabel("");
    setEditClassMode((prevState) => ({
      ...prevState,
      [oldLabel]: false,
    }));
  };

  const handleChange = async (oldLabel: string, index: number, updatedClassItem: string) => {
    const updatedClassList = classList.map((label) => (label === oldLabel ? updatedClassItem : label));

    setClassList(updatedClassList);
    try {
      await editProjectClass(Number(id), index, updatedClassItem);
    } catch (error) {
      console.error(error);
    }
  };

  // 스크롤 이벤트
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault(); // 확대 방지
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  const ITEM_WIDTH = 360 + 24;
  const SCROLL_AMOUNT = ITEM_WIDTH * 3;
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateButtonVisibility = () => {
    const el = scrollRef.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    updateButtonVisibility();

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateButtonVisibility);
    return () => el.removeEventListener("scroll", updateButtonVisibility);
  }, [classList.length]);

  const scrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
  };

  const shouldShowButtons = classList.length > 3;

  return (
    <div style={{ height: "100%", backgroundColor: "#F7F9FC" }}>
      {loading ? (
        <Box
          sx={{
            border: "1px solid var(--dk-border, #eee)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh",
            width: "100%",
            borderRadius: 3,
            mt: 2,
            mb: 2,
            backgroundColor: "var(--dk-surface, #fff)",
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2, color: "var(--dk-text-2, #666)" }}>데이터를 로딩중입니다...</Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative", width: "100%" }}>
          {/* 좌우 스크롤 버튼 */}
          {shouldShowButtons && showLeft && (
            <IconButton onClick={scrollLeft} sx={{ ...arrowButtonStyle, left: 10 }}>
              <ArrowBackIosIcon />
            </IconButton>
          )}
          {shouldShowButtons && showRight && (
            <IconButton onClick={scrollRight} sx={{ ...arrowButtonStyle, right: 10 }}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}

          {/* 학습 상태 카드 */}
          {trainStatus.project_id === Number(id) && trainStatus.state !== "end" && (
            <Box
              sx={{
                position: "absolute",
                top: 20,
                left: 20,
                width: 260,
                zIndex: 10,
                borderRadius: 3,
                p: 2,
                backdropFilter: "blur(10px)",
                background: "var(--dk-surface-glass, rgba(255,255,255,0.9))",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                border: "1px solid var(--dk-border, #eee)",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>학습 상태</Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 13 }}>
                  {trainStatus.state === "train"
                    ? "학습 진행중"
                    : trainStatus.state === "init"
                      ? "대기중"
                      : trainStatus.state === "calc"
                        ? "계산중"
                        : trainStatus.state}
                </Typography>

                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  {((trainStatus.train_time / trainStatus.total_time) * 100 || 0).toFixed(1)}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={(trainStatus.train_time / trainStatus.total_time) * 100 || 0}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 1,
                }}
              />

              <Typography sx={{ fontSize: 12, color: "var(--dk-text-3, #888)" }}>
                남은시간: {formatHourByTime(trainStatus.total_time - trainStatus.train_time)}
              </Typography>
            </Box>
          )}

          {isPolling && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <LinearProgress
                sx={{
                  height: 3,
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #5b94ff, #22C55E)",
                  },
                  backgroundColor: "rgba(91,148,255,0.12)",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 0.75,
                    borderRadius: 999,
                    backgroundColor: "var(--dk-surface-glass, rgba(255,255,255,0.9))",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                    border: "1px solid #E5EAF5",
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <CircularProgress size={14} thickness={5} sx={{ color: "#5b94ff" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: "var(--dk-text-2, #4B5563)" }}>
                    새로운 데이터를 불러오는 중입니다…
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          <Box
            id="ana-class-table"
            ref={scrollRef}
            sx={{
              display: "flex",
              gap: 3,
              px: 4,
              py: 6,
              overflowX: "auto",
              overflowY: "hidden",
              height: "900px",
              scrollSnapType: "x mandatory",

              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {imageList.map((item, index) => (
              // <Box
              //   key={item.label}
              //   sx={{
              //     width: 340,
              //     height: 620,
              //     borderRadius: 3,
              //     flexShrink: 0,
              //     scrollSnapAlign: "start",
              //     backgroundColor: "var(--dk-surface, #fff)",
              //     boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
              //     border: "1px solid var(--dk-border, #eee)",
              //     display: "flex",
              //     flexDirection: "column",
              //     transition: "all 0.2s ease",
              //     "&:hover": {
              //       transform: "translateY(-4px)",
              //       boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
              //     },
              //     overflow: "hidden",
              //   }}
              // >
              //   <Box
              //     sx={{
              //       px: 2,
              //       py: 1.5,
              //       borderBottom: "1px solid var(--dk-border, #eee)",
              //       display: "flex",
              //       justifyContent: "space-between",
              //       alignItems: "center",
              //       background: "linear-gradient(135deg, #F0F4FF 0%, #F9FAFF 100%)",
              //       flexShrink: 0,
              //       position: "relative",
              //       "&::before": {
              //         content: '""',
              //         position: "absolute",
              //         left: 0,
              //         top: 0,
              //         bottom: 0,
              //         width: "4px",
              //         borderTopLeftRadius: 12,
              //         borderBottomLeftRadius: 12,
              //         backgroundColor: ["#4F46E5", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4"][index % 5],
              //       },
              //     }}
              //   >
              //     <Box sx={{ ml: 1 }}>
              //       <Typography sx={{ fontSize: 14, fontWeight: 600, color: "var(--dk-text, #1F2937)" }}>{item.label} Class</Typography>
              //       <Typography sx={{ fontSize: 12, color: "var(--dk-text-2, #6B7280)" }}>{item.count}장</Typography>
              //     </Box>
              //     <Button
              //       id="ana-table-enter"
              //       variant="contained"
              //       size="small"
              //       onClick={() => handleDetailClick(item.label)}
              //       sx={{
              //         backgroundColor: "#5b94ff",
              //         "&:hover": { backgroundColor: "#5b94ff" },
              //       }}
              //     >
              //       {t("Detail")}
              //     </Button>
              //   </Box>

              //   <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
              //     <Table>
              //       <TableBody>
              //         {item.images?.map((image: SimpleImageByClass) => (
              //           <TableRow
              //             key={image.id}
              //             onClick={() => handleRowClick(image)}
              //             sx={{
              //               cursor: "pointer",
              //               transition: "background 0.15s",
              //               "&:hover": { backgroundColor: "#F5F7FB" },
              //             }}
              //           >
              //             <TableCell sx={{ color: "var(--dk-text-2, #555)", width: 80 }}>{image.id}</TableCell>
              //             <TableCell sx={{ color: "var(--dk-text, #333)" }}>
              //               {truncateString(extractFileName(image.path), 20)}
              //             </TableCell>
              //           </TableRow>
              //         ))}
              //       </TableBody>
              //     </Table>
              //   </TableContainer>
              // </Box>
              <ClassImageList
                key={item.label}
                item={item}
                index={index}
                onDetail={handleDetailClick}
                onRow={handleRowClick}
                t={t}
              />
            ))}
          </Box>

          {openPreview && <PreviewModal />}
          {openStorageModal && (
            <AnalysisServerModal openStorageModal={openStorageModal} setOpenStorageModal={setOpenStorageModal} />
          )}
        </Box>
      )}
    </div>
  );
}
